import { useEffect, useMemo, useState } from 'react';
import { UnitSelectorPosition } from 'views/templates/module/unitSelector';
import type { LabelValueUnitSelectorProps } from 'views/templates/module/unitSelector';
import type { SelectorOptionType } from 'views/templates/module/selector';
import type { SpinboxProps } from 'views/mutual/spinbox';
import type { DomesticRealEstateSymbolDetailInfo } from 'views/utils/graphql/graphql';

type UseLotInputAmountControlParams = {
  disabled: boolean | undefined;
  error: string | undefined;
  onChange: ((value: string) => void) | undefined;
  currentAmount: number | null | undefined;
  amountOptionsRaw: Array<string | number>;
  step: number;
  maxQuantity: number;
  unit: string;
};

export type LotInputAmountControlResult = {
  unitSelectorProps: LabelValueUnitSelectorProps;
  spinboxProps: SpinboxProps;
  showManualQuantity: boolean;
};

export const useLotInputAmountControl = ({
  disabled,
  error,
  onChange,
  currentAmount,
  amountOptionsRaw,
  step,
  maxQuantity,
  unit,
}: UseLotInputAmountControlParams) => {
  const [selectedAmount, setSelectedAmount] = useState<SelectorOptionType | null>(null);
  const [inputAmount, setInputAmount] = useState<number | null>(null);
  const [showManualQuantity, setShowManualQuantity] = useState<boolean>(false);
  const toNumeric = (value: string | number) => {
    const normalized = String(value).replaceAll(',', '');
    const num = Number(normalized);
    return Number.isFinite(num) ? num : null;
  };
  const formatWithComma = (value: string | number) => {
    const numeric = toNumeric(value);
    if (numeric === null) {
      return String(value);
    }
    return numeric.toLocaleString();
  };

  const limitValue = amountOptionsRaw.length > 0 ? toNumeric(amountOptionsRaw[amountOptionsRaw.length - 1]) : null;
  const limit = limitValue ?? 0;
  const manualInputMin = limit + step;
  const manualInputMax = maxQuantity;
  const amountOptionsRawKey = amountOptionsRaw.map((option) => String(option)).join(',');

  const amountOptions: SelectorOptionType[] = useMemo(
    () =>
      amountOptionsRaw
        ? [
            ...amountOptionsRaw.map((option) => ({ label: `${formatWithComma(option)}${unit}`, value: option })),
            ...(manualInputMax && limit < manualInputMax
              ? [{ label: `${formatWithComma(manualInputMin)}${unit}以上は手入力をお願いします`, value: manualInputMin }]
              : []),
          ]
        : [],
    [amountOptionsRawKey, limit, manualInputMax, manualInputMin, unit],
  );

  useEffect(() => {
    if (currentAmount == null) {
      setSelectedAmount(null);
      setInputAmount(null);
      setShowManualQuantity(false);
      return;
    }

    const isManual = limit < currentAmount;
    if (isManual) {
      const manualOption = amountOptions.find((option) => toNumeric(option.value) === manualInputMin) ?? null;
      setSelectedAmount(manualOption);
      setInputAmount(currentAmount);
      setShowManualQuantity(true);
      return;
    }

    const selected = amountOptions.find((option) => toNumeric(option.value) === currentAmount) ?? null;
    setSelectedAmount(selected);
    setInputAmount(null);
    setShowManualQuantity(false);
  }, [amountOptions, amountOptionsRawKey, currentAmount, limit, manualInputMin]);

  const amountProps: LotInputAmountControlResult = {
    unitSelectorProps: {
      options: amountOptions,
      unit: UnitSelectorPosition.after,
      label: '',
      selectedOption: selectedAmount?.value?.toString() ?? '',
      setSelectedOption: (value: string) => {
        onChange?.(value);
        const selected = amountOptions.find((option) => {
          //文字列をそのまま比較
          if (option.value.toString() === value) {
            return true;
          }
          //カンマなどを除いて比較
          const optionNumeric = toNumeric(option.value);
          const valueNumeric = toNumeric(value);
          return optionNumeric !== null && valueNumeric !== null && optionNumeric === valueNumeric;
        });
        setSelectedAmount(selected ?? null);
        const valueNum = toNumeric(value) ?? 0;
        const isShowManualQuantity = limit < valueNum;
        setShowManualQuantity(isShowManualQuantity);
        setInputAmount(isShowManualQuantity ? manualInputMin : null);
      },
      disabled,
      error,
    },
    spinboxProps: {
      name: 'symbolSpinbox',
      value: inputAmount ?? manualInputMin,
      onChange: (value: number | null) => {
        if (value === null) {
          setInputAmount(null);
          return;
        }
        let nextValue = value;
        if (manualInputMax && manualInputMax < nextValue) {
          nextValue = manualInputMax;
        }
        if (manualInputMin && nextValue < manualInputMin) {
          nextValue = manualInputMin;
        }
        setInputAmount(nextValue);
        onChange?.(String(nextValue));
      },
      onBlur: async () => {
        let newAmount = manualInputMin;

        if (inputAmount && manualInputMax && manualInputMax < inputAmount) {
          newAmount = manualInputMax;
        } else if (inputAmount && manualInputMin && manualInputMin < inputAmount) {
          newAmount = Math.floor(inputAmount / step) * step;
        }
        onChange?.(String(newAmount));
        setInputAmount(newAmount);
      },
      min: manualInputMin,
      max: manualInputMax ?? undefined,
      step,
      unit,
    },
    showManualQuantity,
  };

  return { amountProps };
};
